import type { ConfirmProjectLinkOutcome } from './staging-types'

/**
 * ADR-0130 (UID-425 Gap 2) — the SINGLE place that decides whether a confirm
 * write's `projectLink` outcome deserves a user-facing warning, and what it
 * says. All FOUR confirm call sites in `StagingPage.tsx`
 * (`handleConfirmToLibrary`, `handleReviewInLibraryConfirmed`,
 * `handleAddToBom`, `handleCreateRfqConfirmed`) call this ONE function rather
 * than each re-deciding it — hand-rolling the same decision four times is
 * exactly the "structural mistake, hand-rolled twice, both copies wrong"
 * ADR-0124 Gate-5 W1 records, just with two more call sites.
 *
 * `not_requested` and `linked` return `undefined` — neither is a warning
 * condition: `not_requested` means the session was never filed (nothing to
 * warn about), and `linked` means the write AND the filing both succeeded.
 *
 * `not_linked` is the ONE case that warns: the confirm write itself
 * succeeded (parts ARE in the Library) but the project link did not
 * complete. This is NOT an error — callers must not route it through an
 * error state, must not block, and must not revert anything.
 *
 * `workspaceName` is passed in from `triage.workspaceName`, which is
 * MEMBERSHIP-GATED (ADR-0114 D6) and can be absent even for a session that
 * IS filed — absence means "no name available to you", NOT "unfiled". So an
 * absent name here falls back to neutral wording ("the selected project"),
 * never to "unfiled" copy: `projectLink.status === 'not_linked'` already
 * tells us the session WAS filed (that's what there was to link).
 */
export function projectLinkWarningMessage(
  projectLink: ConfirmProjectLinkOutcome,
  workspaceName: string | undefined,
): string | undefined {
  if (projectLink.status !== 'not_linked') return undefined
  const projectPhrase = workspaceName ? `“${workspaceName}”` : 'the selected project'
  return `These parts were added to your library, but couldn’t be filed under ${projectPhrase}. You can file them from the Parts Library.`
}
